// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { I18n } from '@calendly/i18n'
import { Badge } from '@calendly/ui/components/badge'
import { BlockIcon } from '@calendly/ui/components/icons'
import { InlineBanner } from '@calendly/ui/components/inline-banner'
import { MinimalButton } from '@calendly/ui/components/minimal-button'
import { PageHeader } from '@calendly/ui/components/page-header'

import { useGetUserQuery } from 'common'
import { LoadingWrapper } from 'components/core/loading_state'
import { SchedulingAssistantTab } from 'features/account_settings'
import { useIsSettingsModalLayout } from 'features/settings_modal/layout_context'
import {
  useDisableEmailAgentMutation,
  useFetchSchedulingAssistantSettingsQuery,
  usePostSchedulingAssistantSettingMutation,
  SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME,
  SCHEDULING_ASSISTANT_ENABLED_SETTING_TYPE,
} from 'hooks/scheduling_assistant/api'
import { getSchedulingAssistantSettingValueByName } from 'hooks/scheduling_assistant/helpers'

export const SchedulingAssistantPage = () => {
  const inSettingsModal = useIsSettingsModalLayout()
  const { data: userData, isLoading: isUserLoading } = useGetUserQuery()
  const { email, uuid: userUuid } = userData ?? {}

  const {
    data: settings,
    isLoading: areSchedulingAssistantSettingsLoading,
    isFetching: areSchedulingAssistantSettingsFetching,
    isError: hasSchedulingAssistantSettingsError
  } = useFetchSchedulingAssistantSettingsQuery(userUuid, { skip: !userUuid })

  const isSettingsPending = areSchedulingAssistantSettingsLoading
    || (areSchedulingAssistantSettingsFetching && !settings)

  const [postSchedulingAssistantSetting, { isLoading: isDisableSettingLoading }] = usePostSchedulingAssistantSettingMutation({
    fixedCacheKey: 'scheduling-assistant-disable',
  })
  const [disableEmailAgent, { isLoading: isDisableEmailAgentLoading }] = useDisableEmailAgentMutation()

  const isSchedulingAssistantEnabled = getSchedulingAssistantSettingValueByName(
    settings,
    SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME
  ) === 'true'

  const handleDisable = async () => {
    if (!userUuid) return
    try {
      await disableEmailAgent().unwrap()
      // Email agent is disabled. OAuth tokens were revoked
      // and deleted downstream in caf-email. Flip the enabled setting
      // to false now, so the UI reflects the change.
      await postSchedulingAssistantSetting({
        userUuid,
        settingName: SCHEDULING_ASSISTANT_ENABLED_SETTING_NAME,
        settingValue: 'false',
        settingType: SCHEDULING_ASSISTANT_ENABLED_SETTING_TYPE
      }).unwrap()
    } catch (error) {
      // No special error handling needed here
    }
  }

  const actions = isSchedulingAssistantEnabled ? [
    <MinimalButton
      key="disable"
      decoration="danger"
      onClick={handleDisable}
      loading={isDisableEmailAgentLoading || isDisableSettingLoading}
      disabled={!userUuid || isSettingsPending}
    >
      <BlockIcon />
      {I18n.t('publisher.personal_settings.scheduling_assistant.enabled.disable_button')}
    </MinimalButton>
  ] : []

  const headerBadges = isSchedulingAssistantEnabled
    ? (
      <>
        <Badge type="new">Beta</Badge>
        <Badge type="success">{I18n.t('publisher.personal_settings.scheduling_assistant.enabled.badge_label')}</Badge>
      </>
    )
    : (
      <>
        <Badge type="new">Beta</Badge>
        <Badge type="error">{I18n.t('publisher.personal_settings.scheduling_assistant.disabled.badge_label')}</Badge>
      </>
    )

  const enabledSubtitle = isSchedulingAssistantEnabled
    ? I18n.t('publisher.personal_settings.scheduling_assistant.enabled.subtitle', { user_email: email ?? '' })
    : null

  return (
    <LoadingWrapper isLoading={isUserLoading || isSettingsPending}>
      {hasSchedulingAssistantSettingsError ? (
        <InlineBanner
          variant="danger"
          title={I18n.t('publisher.personal_settings.scheduling_assistant.settings_load_error_title')}
          message={I18n.t('publisher.personal_settings.scheduling_assistant.settings_load_error_message')}
          isDismissible={false}
        />
      ) : (
        <>
          {!inSettingsModal && (
            <PageHeader
              actions={actions}
              eyebrow={I18n.t('publisher.personal_settings.navigation.account_settings')}
              title={I18n.t('publisher.personal_settings.scheduling_assistant.page_header')}
              badge={headerBadges}
              description={enabledSubtitle}
            />
          )}
          <SchedulingAssistantTab
            userUuid={userUuid}
            email={email}
            settings={settings}
          />
        </>
      )}
    </LoadingWrapper>
  )
}
